import { Component, Input, Output, EventEmitter, signal, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AppSettings } from '@services/settings/app-settings.service';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';
import { SheetSelectComponent } from '../sheet-select/sheet-select.component';

export interface FilterField {
  key: string;
  label: string;
}

export interface FilterRule {
  id: number;
  field: string | null;
  value: string;
  availableFields?: FilterField[];
}

@Component({
  selector: 'app-advanced-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, BottomSheetComponent, SheetSelectComponent],
  templateUrl: './advanced-filter.component.html',
  styleUrls: ['./advanced-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedFilterComponent {
  readonly appSettings = inject(AppSettings);
  @Input() fields: FilterField[] = [];
  @Input() align: 'left' | 'right' = 'left';
  @Input() showTrigger = true;
  @Output() filtersChange = new EventEmitter<Array<{ field: string; value: string }>>();

  isOpen = signal<boolean>(false);
  rules = signal<FilterRule[]>([]);

  private ruleCounter = 0;

  activeFiltersCount = computed(() => {
    return this.rules().filter((r) => r.field && r.value?.trim()).length;
  });

  // Abre el panel y añade regla inicial si no existe ninguna
  open(): void {
    this.isOpen.set(true);
    if (this.rules().length === 0 && this.hasAvailableFields()) {
      this.addRule();
    } else {
      this.updateAvailableFields();
    }
  }

  // Cierra el panel flotante de filtros
  close(): void {
    this.isOpen.set(false);
  }

  // Alterna la visibilidad del panel flotante
  toggleOpen(): void {
    this.isOpen() ? this.close() : this.open();
  }

  // Verifica si aún quedan campos disponibles para agregar
  hasAvailableFields(): boolean {
    const chosen = this.rules().map((r) => r.field).filter(Boolean);
    return this.fields.some((f) => !chosen.includes(f.key));
  }

  // Recalcula campos disponibles de forma estable por regla
  updateAvailableFields(): void {
    const rules = this.rules();
    const updated = rules.map((r) => {
      const chosen = rules.filter((other) => other.id !== r.id && other.field).map((o) => o.field);
      const available = this.fields.filter((f) => f.key === r.field || !chosen.includes(f.key));
      return { ...r, availableFields: available };
    });
    this.rules.set(updated);
  }

  // Agrega una nueva regla con el primer campo disponible
  addRule(): void {
    if (!this.hasAvailableFields()) return;
    const chosen = this.rules().map((r) => r.field).filter(Boolean);
    const firstFree = this.fields.find((f) => !chosen.includes(f.key));
    const newRule: FilterRule = { id: ++this.ruleCounter, field: firstFree?.key ?? null, value: '' };
    this.rules.update((list) => [...list, newRule]);
    this.updateAvailableFields();
    this.emitChange();
  }

  // Elimina una regla específica por su ID
  removeRule(ruleId: number): void {
    this.rules.update((list) => list.filter((r) => r.id !== ruleId));
    this.updateAvailableFields();
    this.emitChange();
  }

  // Limpia todas las reglas activas
  clearRules(): void {
    this.rules.set([]);
    this.emitChange();
  }

  // Notifica los cambios de reglas válidas al componente padre
  emitChange(): void {
    const active = this.rules()
      .filter((r) => r.field && r.value?.trim())
      .map((r) => ({ field: r.field as string, value: r.value.trim() }));
    this.filtersChange.emit(active);
  }

  // Maneja cambios de valor o campo en una regla
  onRuleChange(): void {
    this.updateAvailableFields();
    this.emitChange();
  }
}
